import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, Download, Loader2, Search, FileText, X } from 'lucide-react';
import { securitfeeAPI, securityFeeConfigAPI, formatCurrency } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { buildCsvRaw, downloadCsv } from '../utils/csvExport';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const DEFAULT_FEE_RATE_KIP = 1000;
const MBB_RATE_KIP = 3000;
const FBB_RATE_KIP = 5000;

// A4 content width in px (matches PDF export: 210mm - 2×20mm margin => ~972px in capture)
const A4_CONTENT_WIDTH_PX = 972;

const ReportInvoiceByOperator = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [operator, setOperator] = useState('UNITEL');
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedOperator, setAppliedOperator] = useState(null);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);
  const [isExporting, setIsExporting] = useState(false);
  const [showOfficialInvoice, setShowOfficialInvoice] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  useEffect(() => {
    loadOperatorOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to load operator list
  }, []);

  const loadOperatorOptions = async () => {
    try {
      const fallbackOps = await securityFeeConfigAPI.getOperators().catch(() => ({ data: [] }));
      const configOps = (fallbackOps?.data || []).map(o => o.operator_name || o.name || o).filter(Boolean);
      if (configOps.length > 0) {
        setOperatorOptions(configOps);
        if (!operator || !configOps.includes(operator)) setOperator(configOps[0]);
      } else {
        const defaultOps = ['UNITEL', 'LTC', 'TPLUS', 'ETL', 'BEST'];
        setOperatorOptions(defaultOps);
      }
    } catch {
      setOperatorOptions(['UNITEL', 'LTC', 'TPLUS', 'ETL', 'BEST']);
    }
  };

  const handleSearch = () => {
    setAppliedOperator(operator);
    setAppliedMonth(month);
    setAppliedYear(year);
    setShowOfficialInvoice(false);
    setInvoiceNumber('');
  };

  useEffect(() => {
    if (appliedOperator == null || appliedOperator === '') return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when applied filters change
  }, [appliedOperator, appliedMonth, appliedYear]);

  const fetchData = async () => {
    if (appliedOperator == null) return;
    try {
      setLoading(true);
      setError(null);
      const params = { month: appliedMonth, year: appliedYear };
      const [byOpRes, configRes] = await Promise.all([
        securitfeeAPI.getByOperator(params),
        securityFeeConfigAPI.getAll().catch(() => null)
      ]);

      let rawList = byOpRes?.data;
      if (!Array.isArray(rawList) && byOpRes?.data && typeof byOpRes.data === 'object') {
        rawList = byOpRes.data.by_operator ?? byOpRes.data.byOperator ?? byOpRes.data.results ?? [];
      }
      if (!Array.isArray(rawList)) rawList = Array.isArray(byOpRes) ? byOpRes : [];
      const configs = configRes?.data?.configurations || configRes?.configurations || [];
      const getRate = (op, serviceType) => {
        const cfg = configs.find(c => (c.operator_name || c.operator_id || '').toString() === op && (c.serviceType || '').toLowerCase() === serviceType);
        if (cfg?.feeAmount != null) return Number(cfg.feeAmount);
        return serviceType === 'mbb' ? MBB_RATE_KIP : FBB_RATE_KIP;
      };

      const list = rawList.map(item => {
        const opName = item.operatorName || item.operator_name || item.operator;
        const collected = Number(item.collectedAmount ?? item.collected ?? item.collected_amount ?? 0) || 0;
        const remaining = Number(item.remainingAmount ?? item.remaining ?? item.remaining_amount ?? 0) || 0;
        const expectedAmount = item.expectedAmount != null ? Number(item.expectedAmount) : (item.expected_amount != null ? Number(item.expected_amount) : null);
        const mbbSubs = Number(item.mbbSubscribers ?? item.mbbEligible ?? item.mbb_eligible ?? item.mbb_subscribers ?? item.mbb_count ?? 0) || 0;
        const fbbSubs = Number(item.fbbSubscribers ?? item.fbbEligible ?? item.fbb_eligible ?? item.fbb_subscribers ?? item.fbb_count ?? 0) || 0;
        let eligible = Number(
          item.eligibleSubscribers ?? item.eligible ?? item.eligible_subscribers
          ?? item.eligible_subscriber_count ?? item.activeSubscriberCount ?? item.active_subscriber_count
          ?? item.total_eligible ?? item.subscriber_count ?? 0
        ) || 0;
        const mbbAmount = item.mbbAmount != null ? Number(item.mbbAmount) : (item.mbb_amount != null ? Number(item.mbb_amount) : (mbbSubs * getRate(opName, 'mbb')));
        const fbbAmount = item.fbbAmount != null ? Number(item.fbbAmount) : (item.fbb_amount != null ? Number(item.fbb_amount) : (fbbSubs * getRate(opName, 'fbb')));
        if (eligible === 0 && (mbbSubs > 0 || fbbSubs > 0)) eligible = mbbSubs + fbbSubs;
        if (eligible === 0 && expectedAmount != null && expectedAmount > 0) {
          const rate = getRate(opName, 'mbb') || getRate(opName, 'fbb') || DEFAULT_FEE_RATE_KIP;
          if (rate > 0) eligible = Math.round(expectedAmount / rate);
        }
        const hasServiceBreakdown = mbbSubs > 0 || fbbSubs > 0 || (eligible > 0 && mbbSubs === 0 && fbbSubs === 0);
        const mbbRate = getRate(opName, 'mbb');
        const fbbRate = getRate(opName, 'fbb');
        return {
          operator: opName,
          eligible,
          collected,
          remaining,
          expectedAmount,
          mbbSubscribers: mbbSubs,
          fbbSubscribers: fbbSubs,
          mbbAmount,
          fbbAmount,
          mbbRate,
          fbbRate,
          hasServiceBreakdown: hasServiceBreakdown && (mbbSubs > 0 || fbbSubs > 0)
        };
      });

      const selectedOp = appliedOperator;
      let rate = DEFAULT_FEE_RATE_KIP;
      if (configs.length > 0) {
        const forOperator = configs.find(c => (c.operator_name || c.operator_id || '').toString() === selectedOp);
        const cfg = forOperator || configs[0];
        if (cfg?.feeAmount != null) rate = Number(cfg.feeAmount) || rate;
      }

      const current = list.find(x => x.operator === selectedOp) || {
        operator: selectedOp || '',
        eligible: 0,
        collected: 0,
        remaining: 0,
        expectedAmount: null,
        mbbSubscribers: 0,
        fbbSubscribers: 0,
        mbbAmount: 0,
        fbbAmount: 0,
        hasServiceBreakdown: false
      };
      const expected = current.expectedAmount != null
        ? current.expectedAmount
        : (current.eligible * rate);
      const displayRate = current.expectedAmount != null && current.eligible > 0
        ? current.expectedAmount / current.eligible
        : rate;
      setInvoices([{
        ...current,
        expected,
        rate: displayRate,
        mbbRate: current.mbbRate ?? getRate(selectedOp, 'mbb'),
        fbbRate: current.fbbRate ?? getRate(selectedOp, 'fbb'),
      }]);
    } catch (err) {
      console.error(err);
      setError(err.message || t('reportInvoice.loadError'));
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = () => {
    const m = appliedMonth || month;
    const y = appliedYear || year;
    setInvoiceNumber(`INV-${(appliedOperator || operator || 'OP').toUpperCase()}-${y}${String(m).padStart(2, '0')}-${Date.now().toString(36).toUpperCase()}`);
    setShowOfficialInvoice(true);
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const m = appliedMonth || month;
    const y = appliedYear || year;
    const monthName = months.find(mo => mo.value === m)?.label || 'Current';
    const op = appliedOperator || operator || 'operator';
    const filename = `invoice-${String(op).toLowerCase()}-${monthName.toLowerCase()}-${y}`;
    const result = await PDFExporter.exportToPDF('official-invoice-block', filename, { scale: 1.5, marginMm: 20 });
    if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    setIsExporting(false);
  };

  const invoice = invoices[0] || {
    operator: appliedOperator || operator || '',
    eligible: 0,
    collected: 0,
    remaining: 0,
    expected: 0,
    rate: DEFAULT_FEE_RATE_KIP,
    mbbSubscribers: 0,
    fbbSubscribers: 0,
    mbbAmount: 0,
    fbbAmount: 0,
    mbbRate: MBB_RATE_KIP,
    fbbRate: FBB_RATE_KIP,
    hasServiceBreakdown: false
  };
  const mbbDisplayRate = invoice.mbbRate ?? MBB_RATE_KIP;
  const fbbDisplayRate = invoice.fbbRate ?? FBB_RATE_KIP;
  const expectedAmount = invoice.expected != null ? invoice.expected : (invoice.eligible * (invoice.rate || DEFAULT_FEE_RATE_KIP));
  const displayMonth = appliedMonth || month;
  const displayYear = appliedYear || year;
  const hasSearched = appliedOperator != null && appliedOperator !== '';
  const periodLabel = hasSearched ? `${months.find(m => m.value === displayMonth)?.label ?? displayMonth} ${displayYear}` : '';

  const handleExportCSV = () => {
    const issued = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Invoice by Operator', ''],
      ['Invoice Number', showOfficialInvoice ? invoiceNumber : ''],
      ['Operator', invoice.operator],
      ['Period', periodLabel],
      ['Issued', issued],
      [],
      ['Description', 'Qty', 'Rate (Kip)', 'Amount (Kip)'],
    ];
    if ((invoice.mbbSubscribers ?? 0) > 0 || (invoice.fbbSubscribers ?? 0) > 0) {
      if ((invoice.mbbSubscribers ?? 0) > 0) {
        rows.push(['Security fee – MBB (Mobile Broadband)', String(invoice.mbbSubscribers ?? 0), String(mbbDisplayRate), String(invoice.mbbAmount ?? 0)]);
      }
      if ((invoice.fbbSubscribers ?? 0) > 0) {
        rows.push(['Security fee – FBB (Fixed Broadband / PSTN)', String(invoice.fbbSubscribers ?? 0), String(fbbDisplayRate), String(invoice.fbbAmount ?? 0)]);
      }
    } else {
      rows.push(['Security fee – eligible subscribers', String(invoice.eligible ?? 0), String(invoice.rate || DEFAULT_FEE_RATE_KIP), String(expectedAmount)]);
    }
    rows.push([], ['Total due / Expected', '', '', String(expectedAmount)]);
    rows.push(['Collected', '', '', String(invoice.collected ?? 0)]);
    rows.push(['Balance due / Remaining', '', '', String(invoice.remaining ?? 0)]);
    const csv = buildCsvRaw(rows);
    const op = (appliedOperator || operator || 'operator').toString().toLowerCase();
    const monthName = (months.find(m => m.value === displayMonth)?.label || 'current').toLowerCase();
    downloadCsv(`invoice-${op}-${monthName}-${displayYear}`, csv);
  };

  return (
    <div id="report-invoice-operator" className="min-h-screen bg-gray-50/60">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Page header */}
        <header>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600">
              <Receipt className="h-5 w-5" />
            </span>
            {t('reportInvoice.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1.5 ml-[52px]">{t('reportInvoice.subtitle')}</p>
        </header>

        {/* Filters */}
        <section className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
          <div className="px-6 py-5">
            <p className="text-sm font-medium text-gray-700 mb-4">{t('reportInvoice.selectOperatorPeriod')}</p>
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('common.operator')}</label>
                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50/50 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                >
                  {operatorOptions.map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[130px]">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('common.month')}</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50/50 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('common.year')}</label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50/50 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  min="2020"
                  max="2030"
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    {t('reportInvoice.loadInvoice')}
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {error && (
          <div className="bg-red-50/80 border border-red-100 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
            <p className="text-sm text-red-800">{error}</p>
            <button
              type="button"
              onClick={handleSearch}
              className="shrink-0 text-sm font-medium text-red-600 hover:text-red-800 underline"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        )}

        {!loading && !hasSearched && (
          <section className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Receipt className="h-7 w-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-medium">{t('reportInvoice.noInvoiceYet')}</p>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              {t('reportInvoice.noInvoiceHintBefore')}<strong>{t('reportInvoice.loadInvoice')}</strong>{t('reportInvoice.noInvoiceHintAfter')}
            </p>
          </section>
        )}

        {!loading && hasSearched && invoices.length === 0 && !error && (
          <section className="bg-amber-50/80 border border-amber-100 rounded-2xl p-8 text-center">
            <p className="text-amber-800 font-medium">{t('reportInvoice.noData')}</p>
            <p className="text-sm text-amber-700/90 mt-1">{t('reportInvoice.noDataHint')}</p>
          </section>
        )}

        {!loading && hasSearched && invoices.length > 0 && (
          <>
            {/* Summary + CTA */}
            <section className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
              <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {invoice.operator} · {t(`common.monthNames.${displayMonth}`)} {displayYear}
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-600">
                    <span>{t('reportInvoice.eligible')}: <strong className="text-gray-900">{Number(invoice.eligible).toLocaleString()}</strong></span>
                    <span>{t('reportInvoice.expected')}: <strong className="text-gray-900">{formatCurrency(expectedAmount)}</strong></span>
                    <span>{t('reportInvoice.collected')}: <strong className="text-gray-900">{formatCurrency(invoice.collected)}</strong></span>
                    <span>{t('reportInvoice.remaining')}: <strong className="text-gray-900">{formatCurrency(invoice.remaining)}</strong></span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleGenerateInvoice}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-2 transition-colors shadow-sm shrink-0"
                >
                  <FileText className="h-4 w-4" />
                  {t('reportInvoice.generateInvoice')}
                </button>
              </div>
            </section>

            {/* Fee breakdown */}
            <section className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">{t('reportInvoice.feeBreakdown')}</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="rounded-xl border border-gray-100 bg-slate-50/50 p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('reportInvoice.mbbLabel')}</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('common.subscribers')}</dt>
                        <dd className="font-medium text-gray-900">{(invoice.mbbSubscribers ?? 0).toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('reportInvoice.rateKip')}</dt>
                        <dd className="font-medium text-gray-900">{mbbDisplayRate.toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t border-gray-200">
                        <dt className="text-gray-600 font-medium">{t('reportInvoice.amountKip')}</dt>
                        <dd className="font-semibold text-gray-900">{formatCurrency(invoice.mbbAmount ?? 0)}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-emerald-50/30 p-5">
                    <h3 className="text-sm font-semibold text-emerald-800 mb-3">{t('reportInvoice.fbbLabel')}</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('common.subscribers')}</dt>
                        <dd className="font-medium text-gray-900">{(invoice.fbbSubscribers ?? 0).toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('reportInvoice.rateKip')}</dt>
                        <dd className="font-medium text-gray-900">{fbbDisplayRate.toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t border-gray-200">
                        <dt className="text-gray-600 font-medium">{t('reportInvoice.amountKip')}</dt>
                        <dd className="font-semibold text-gray-900">{formatCurrency(invoice.fbbAmount ?? 0)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
                {(invoice.eligible > 0 && (invoice.mbbSubscribers ?? 0) === 0 && (invoice.fbbSubscribers ?? 0) === 0) && (
                  <p className="text-xs text-amber-700 bg-amber-50/80 rounded-xl px-4 py-3 mt-4">{t('reportInvoice.breakdownNotProvided')}</p>
                )}
              </div>
            </section>

          {/* Official Invoice Modal – export targets #official-invoice-block only */}
          {showOfficialInvoice && (
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setShowOfficialInvoice(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="official-invoice-title"
            >
              <div
                className="bg-white rounded-2xl shadow-xl border border-gray-200/80 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{ width: 'min(100vw - 2rem, 1024px)' }}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                  <h2 id="official-invoice-title" className="text-lg font-semibold text-gray-900">
                    {t('reportInvoice.officialInvoiceA4')}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowOfficialInvoice(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="overflow-y-auto flex-1 p-6 flex justify-center">
                  <div
                    id="official-invoice-block"
                    className="bg-white text-gray-900 shadow-inner border border-gray-200"
                    style={{
                      width: A4_CONTENT_WIDTH_PX,
                      maxWidth: '100%',
                      padding: '2rem 2.5rem',
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontSize: '11pt',
                      lineHeight: 1.4,
                    }}
                  >
                    <div className="border-b border-gray-300 pb-4 mb-6">
                      <div style={{ fontSize: '9pt', color: '#6b7280' }}>{t('common.appBrand')}</div>
                      <h3 className="font-bold text-gray-900 mt-1" style={{ fontSize: '14pt' }}>{t('reportInvoice.officialInvoice')}</h3>
                      {invoiceNumber && (
                        <div className="text-gray-600 mt-1" style={{ fontSize: '10pt' }}>{t('reportInvoice.invoiceNo', { value: invoiceNumber })}</div>
                      )}
                      <div className="text-right mt-4 space-y-0.5" style={{ fontSize: '10pt', color: '#6b7280' }}>
                        <div>{t('common.operator')}: <span className="font-semibold text-gray-900">{invoice.operator}</span></div>
                        <div>{t('reportInvoice.period')}: {t(`common.monthNames.${displayMonth}`)} {displayYear}</div>
                        <div>{t('reportInvoice.issued')}: {new Date().toLocaleDateString()}</div>
                      </div>
                    </div>

                    <table className="w-full" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left py-2 font-semibold text-gray-700 border-b border-gray-300" style={{ paddingLeft: 0 }}>{t('reportInvoice.description')}</th>
                          <th className="text-right py-2 font-semibold text-gray-700 border-b border-gray-300">{t('reportInvoice.qty')}</th>
                          <th className="text-right py-2 font-semibold text-gray-700 border-b border-gray-300">{t('reportInvoice.rateKip')}</th>
                          <th className="text-right py-2 font-semibold text-gray-700 border-b border-gray-300">{t('reportInvoice.amountKip')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(invoice.mbbSubscribers > 0 || invoice.fbbSubscribers > 0) ? (
                          <>
                            <tr className="border-b border-gray-200">
                              <td className="py-2.5 text-gray-800" style={{ paddingLeft: 0 }}>{t('reportInvoice.feeMbb')}</td>
                              <td className="py-2.5 text-right">{Number(invoice.mbbSubscribers).toLocaleString()}</td>
                              <td className="py-2.5 text-right">{mbbDisplayRate.toLocaleString()}</td>
                              <td className="py-2.5 text-right">{formatCurrency(invoice.mbbAmount ?? 0)}</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="py-2.5 text-gray-800" style={{ paddingLeft: 0 }}>{t('reportInvoice.feeFbb')}</td>
                              <td className="py-2.5 text-right">{Number(invoice.fbbSubscribers).toLocaleString()}</td>
                              <td className="py-2.5 text-right">{fbbDisplayRate.toLocaleString()}</td>
                              <td className="py-2.5 text-right">{formatCurrency(invoice.fbbAmount ?? 0)}</td>
                            </tr>
                          </>
                        ) : (
                          <tr className="border-b border-gray-200">
                            <td className="py-2.5 text-gray-800" style={{ paddingLeft: 0 }}>{t('reportInvoice.feeEligible')}</td>
                            <td className="py-2.5 text-right">{Number(invoice.eligible).toLocaleString()}</td>
                            <td className="py-2.5 text-right">{(invoice.rate || DEFAULT_FEE_RATE_KIP).toLocaleString()}</td>
                            <td className="py-2.5 text-right">{formatCurrency(expectedAmount)}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="py-2.5 text-right font-semibold text-gray-900" colSpan={3} style={{ paddingLeft: 0 }}>{t('reportInvoice.totalDue')}</td>
                          <td className="py-2.5 text-right font-semibold">{formatCurrency(expectedAmount)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-right text-gray-700" colSpan={3} style={{ paddingLeft: 0 }}>{t('reportInvoice.collected')}</td>
                          <td className="py-2 text-right">{formatCurrency(invoice.collected)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-right font-semibold text-gray-900" colSpan={3} style={{ paddingLeft: 0 }}>{t('reportInvoice.balanceDue')}</td>
                          <td className="py-2 text-right font-semibold">{formatCurrency(invoice.remaining)}</td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="mt-8 text-gray-600" style={{ fontSize: '9pt' }}>
                      <div className="font-semibold text-gray-700 mb-1">{t('reportInvoice.notes')}</div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        <li>{t('reportInvoice.note1')}</li>
                        <li>{t('reportInvoice.note2')}</li>
                        <li>{t('reportInvoice.note3')}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-3 shrink-0 bg-gray-50/50">
                  <button
                    type="button"
                    onClick={() => setShowOfficialInvoice(false)}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportCSV}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    {t('common.exportCSV')}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    disabled={isExporting}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? t('common.exporting') : t('common.exportPDF')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default ReportInvoiceByOperator;
